const LOCAL_API_FALLBACK = 'http://localhost:4000';
const DEFAULT_INSTALLER_PATH = '/downloads/cafe-local-windows.exe';
const DEFAULT_MAC_INSTALLER_PATH = '/downloads/cafe-local-mac.dmg';
const DEFAULT_PORTAL_BASE_URL = 'https://cafe.helfer.coach';
const DEFAULT_HELFER_ONLINE_WEB_URL = 'https://helfer.coach/';
const DEFAULT_EVENTS_WEB_URL = '/';
const DEFAULT_EVENTS_IOS_URL = 'https://apps.apple.com/app/helfer-portal/id6777720132';

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, '');
}

function normalizeBaseUrl(value?: string | null) {
  const normalized = String(value || '').trim();
  return normalized ? trimTrailingSlash(normalized) : '';
}

function normalizePath(value?: string | null, fallback = '/') {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return fallback;
  }

  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }

  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

function getBrowserOrigin() {
  if (typeof window === 'undefined') {
    return '';
  }

  const origin = normalizeBaseUrl(window.location.origin);
  if (!origin || origin === 'null' || origin.startsWith('file://')) {
    return '';
  }

  return origin;
}

function isLocalBrowserOrigin(origin: string) {
  return /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(?::\d+)?$/i.test(origin);
}

function resolveLocalDevApiOrigin(origin: string) {
  if (typeof window === 'undefined' || !origin) {
    return '';
  }

  try {
    const url = new URL(origin);
    const isDevFrontendPort = url.port === '8088' || url.port === '8080';
    const isPrivateLanHost =
      /^(10|127)\./.test(url.hostname) ||
      /^192\.168\./.test(url.hostname) ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(url.hostname) ||
      url.hostname === 'localhost';

    if (!isDevFrontendPort || !isPrivateLanHost) {
      return '';
    }

    return `${url.protocol}//${url.hostname}:4000`;
  } catch {
    return '';
  }
}

function getDesktopRuntime() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.__CAFE_DESKTOP_RUNTIME__ || null;
}

const browserOrigin = getBrowserOrigin();
const desktopRuntime = getDesktopRuntime();
const publicBrowserOrigin = browserOrigin && !isLocalBrowserOrigin(browserOrigin) ? browserOrigin : '';
const localDevApiOrigin = resolveLocalDevApiOrigin(browserOrigin);

export const PORTAL_BASE_URL =
  normalizeBaseUrl(process.env.VUE_APP_PORTAL_BASE_URL) ||
  normalizeBaseUrl(desktopRuntime?.portalBaseUrl) ||
  (!desktopRuntime ? localDevApiOrigin : '') ||
  (!desktopRuntime ? publicBrowserOrigin : '') ||
  DEFAULT_PORTAL_BASE_URL;

export const API_BASE_URL =
  normalizeBaseUrl(process.env.VUE_APP_API_BASE_URL) ||
  normalizeBaseUrl(desktopRuntime?.apiBaseUrl) ||
  localDevApiOrigin ||
  browserOrigin ||
  LOCAL_API_FALLBACK;

export const DESKTOP_INSTALLER_DOWNLOAD_PATH =
  normalizePath(process.env.VUE_APP_DESKTOP_INSTALLER_PATH, DEFAULT_INSTALLER_PATH);

export const DESKTOP_MAC_INSTALLER_DOWNLOAD_PATH =
  normalizePath(process.env.VUE_APP_DESKTOP_INSTALLER_MAC_PATH, DEFAULT_MAC_INSTALLER_PATH);

export function resolvePortalUrl(pathname: string) {
  if (/^https?:\/\//i.test(pathname)) {
    return pathname;
  }

  const normalizedPath = normalizePath(pathname);
  return `${PORTAL_BASE_URL}${normalizedPath}`;
}

export const PORTAL_VERSION_URL = resolvePortalUrl('/api/version');
export const DESKTOP_INSTALLER_DOWNLOAD_URL = resolvePortalUrl(DESKTOP_INSTALLER_DOWNLOAD_PATH);
export const DESKTOP_MAC_INSTALLER_DOWNLOAD_URL = resolvePortalUrl(DESKTOP_MAC_INSTALLER_DOWNLOAD_PATH);

export const HELFER_ONLINE_WEB_URL =
  String(process.env.VUE_APP_HELFER_ONLINE_WEB_URL || '').trim() ||
  DEFAULT_HELFER_ONLINE_WEB_URL;

export const HELFER_ONLINE_IOS_URL =
  String(process.env.VUE_APP_HELFER_ONLINE_IOS_URL || '').trim() ||
  HELFER_ONLINE_WEB_URL;

export const HELFER_ONLINE_ANDROID_URL =
  String(process.env.VUE_APP_HELFER_ONLINE_ANDROID_URL || '').trim() ||
  HELFER_ONLINE_WEB_URL;

export const HELFER_ONLINE_MAC_URL =
  String(process.env.VUE_APP_HELFER_ONLINE_MAC_URL || '').trim() ||
  HELFER_ONLINE_WEB_URL;

export const HELFER_ONLINE_WINDOWS_URL =
  String(process.env.VUE_APP_HELFER_ONLINE_WINDOWS_URL || '').trim() ||
  HELFER_ONLINE_WEB_URL;

export const EVENTS_WEB_URL =
  String(process.env.VUE_APP_EVENTS_WEB_URL || '').trim() ||
  DEFAULT_EVENTS_WEB_URL;

export const EVENTS_IOS_URL =
  String(process.env.VUE_APP_EVENTS_IOS_URL || '').trim() ||
  DEFAULT_EVENTS_IOS_URL;

export const EVENTS_ANDROID_URL =
  String(process.env.VUE_APP_EVENTS_ANDROID_URL || '').trim() ||
  EVENTS_WEB_URL;

export function resolveApiUrl(pathname: string) {
  if (/^https?:\/\//i.test(pathname)) {
    return pathname;
  }

  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

export function resolveUploadedAssetUrl(assetPath?: string | null) {
  if (!assetPath) {
    return '';
  }

  return resolveApiUrl(`/${String(assetPath).replace(/^\/+/, '')}`);
}
